import React from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { ResidentDevice } from 'features/residents/models/resident-device';
import styles from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/device-link-modal/device-link-modal.module.scss';
import useDeviceLinkModal from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/device-link-modal/hooks/use-device-link-modal';
import LinkableTransmitterSteps from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitter-steps/linkable-transmitter-steps';
import LinkableTransmittersTable from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitters-table/linkable-transmitters-table';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onLinkButtonClicked: (selectedDevices: ResidentDevice[]) => void;
};

const DeviceLinkModal = ({ isOpen, onClose, onLinkButtonClicked }: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings');
  const {
    selectedDevices,
    hasDeviceLinkError,
    transmitterResidentSettings,
    isLinkableTransmitterStepsVisible,
    deviceIdToSelect,
    getErrorMessageForDevice,
    handleOnClose,
    handleOnLinkButtonClicked,
    handleOnResetFilters,
    toggleLinkableTransmitterSteps,
    handleTransmitterAdded,
  } = useDeviceLinkModal({
    onClose,
    onLinkButtonClicked,
  });

  const renderLinkableTransmitterSteps = (): React.JSX.Element | undefined =>
    isLinkableTransmitterStepsVisible ? (
      <LinkableTransmitterSteps
        toggleLinkableTransmitterSteps={toggleLinkableTransmitterSteps}
        onTransmitterAdded={handleTransmitterAdded}
        transmitterResidentSettings={transmitterResidentSettings ?? []}
      />
    ) : undefined;

  const shouldShowAddButton = transmitterResidentSettings && transmitterResidentSettings.length > 0;

  return (
    <Dialog
      className={styles.residentDetailsLinkableDevicesModal}
      open={isOpen}
      onClose={handleOnClose}
      slotProps={{ paper: { classes: { root: styles.linkableDevicesModalPaper } } }}
    >
      <div className={styles.linkableDevicesModalContainer}>
        <div className={styles.linkableDevicesModalHeaderContainer}>
          <Typography className={styles.headerTitle} variant="h3">
            {t('linkableDevicesModal.title')}
          </Typography>
          <div className={styles.linkableDevicesModalHeaderActionsContainer}>
            {selectedDevices.length > 0 ? (
              <Button
                className={styles.linkButton}
                variant="contained"
                onClick={handleOnLinkButtonClicked}
                startIcon={<CheckIcon className={styles.checkIcon} />}
                data-testid="linkSelectedDevicesButton"
                disabled={hasDeviceLinkError !== undefined || isLinkableTransmitterStepsVisible}
              >
                {t('linkableDevicesModal.enabledLinkButtonText', {
                  amountOfSelectedDevices: selectedDevices.length,
                })}
              </Button>
            ) : (
              <Button variant="contained" disabled>
                {t('linkableDevicesModal.disabledLinkButtonText')}
              </Button>
            )}

            <Divider orientation="vertical" flexItem />

            <IconButton aria-label="close" className={styles.closeButton} onClick={handleOnClose}>
              <Close />
            </IconButton>
          </div>
        </div>
        <div className={styles.linkableTransmittersTableContainer}>
          <LinkableTransmittersTable
            getErrorMessageForDevice={getErrorMessageForDevice}
            handleOnResetFilters={handleOnResetFilters}
            customFirstRow={renderLinkableTransmitterSteps()}
            disabled={isLinkableTransmitterStepsVisible}
            deviceIdToSelect={deviceIdToSelect}
            isLinkableTransmitterStepsVisible={isLinkableTransmitterStepsVisible}
          />
          {shouldShowAddButton && !isLinkableTransmitterStepsVisible && (
            <Fab
              data-testid="deviceLinkModalAddButton"
              aria-label="add"
              className={styles.addButton}
              onClick={toggleLinkableTransmitterSteps}
            >
              <AddIcon />
            </Fab>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default DeviceLinkModal;
